/**
 * Role hierarchy — the single source of truth for "who may manage whom" on the
 * members surface. Imported by BOTH the backend member endpoints (to enforce
 * the gate) and the web members grid (to mirror it in the UI) so the two can
 * never drift.
 *
 * Pure module — no `better-auth` import — so it can be pulled in via the
 * `@event-manager/api-client/auth/role-rank` subpath without dragging the
 * access-control barrel into the server runtime.
 *
 * The ladder (highest first):
 *   platform superuser  >  org owner  >  org admin  >  org secretary  >  org guest
 *
 * A superuser may manage anyone. Everyone else may manage only members whose
 * org rank is STRICTLY lower than their own — never a peer, never themselves,
 * and never a platform superuser.
 */

import type { MemberRole } from "../common/enums";

/** Org-role → numeric rank. Higher wins. Unknown / no role → 0. */
export const ORG_ROLE_RANK: Record<MemberRole, number> = {
	owner: 4,
	admin: 3,
	secretary: 2,
	guest: 1,
};

/** Rank of an org role string; 0 for null/undefined/unrecognized values. */
export function orgRoleRank(role: string | null | undefined): number {
	if (!role) {
		return 0;
	}
	return ORG_ROLE_RANK[role as MemberRole] ?? 0;
}

export interface ManageMemberInput {
	/** Is the acting user a platform superuser? */
	actorIsSuperuser: boolean;
	/** Acting user's org role (owner/admin/secretary/guest) or null. */
	actorOrgRole: string | null | undefined;
	/** Is the target the acting user themselves? */
	isSelf: boolean;
	/** Is the target member a platform superuser? */
	targetIsSuperuser: boolean;
	/** Target member's org role. */
	targetOrgRole: string | null | undefined;
}

/**
 * Whether `actor` may edit / deactivate / soft-delete the target member.
 *
 * - A superuser may manage anyone (including other superusers and owners).
 * - Otherwise: the target must not be a superuser, must not be the actor
 *   themselves, and the actor's org rank must be STRICTLY greater than the
 *   target's.
 */
export function canManageMember(input: ManageMemberInput): boolean {
	if (input.actorIsSuperuser) {
		return true;
	}
	if (input.targetIsSuperuser || input.isSelf) {
		return false;
	}
	return orgRoleRank(input.actorOrgRole) > orgRoleRank(input.targetOrgRole);
}
